import { Loader2 } from "lucide-react"
import { BsArrowClockwise, BsDownload, BsSearch, BsX } from "react-icons/bs"
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import { useExportUiStore } from "../../store/exportUiStore"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../ui/tooltip"
import { cn } from "../../lib/utils"

type GridSearchInputProps = {
  value: string
  onValueChange: (value: string) => void
  placeholder?: string
  className?: string
  inputClassName?: string
  disabled?: boolean
  "aria-label"?: string
}

export function GridSearchInput({
  value,
  onValueChange,
  placeholder = "Search…",
  className,
  inputClassName,
  disabled = false,
  "aria-label": ariaLabel = "Search",
}: GridSearchInputProps) {
  const hasValue = value.length > 0

  return (
    <div className={cn("tm-toolbar-search relative shrink-0", className)}>
      <BsSearch
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden
      />
      <Input
        type="text"
        value={value}
        onChange={(event) => onValueChange(event.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={ariaLabel}
        className={cn("h-9 pl-9", hasValue && "pr-9", inputClassName)}
      />
      {hasValue && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute right-0 top-0 h-9 w-9 text-muted-foreground hover:text-foreground"
          onClick={() => onValueChange("")}
          disabled={disabled}
          aria-label="Clear search"
        >
          <BsX className="h-4 w-4" />
        </Button>
      )}
    </div>
  )
}

type GridExportButtonProps = {
  onClick: () => void
  disabled?: boolean
  loading?: boolean
  label?: string
  loadingLabel?: string
  className?: string
}

export function GridExportButton({
  onClick,
  disabled = false,
  loading = false,
  label = "Export",
  loadingLabel = "Exporting...",
  className,
}: GridExportButtonProps) {
  const globalExporting = useExportUiStore((state) => state.exporting)
  const isLoading = loading || globalExporting
  const tooltipLabel = isLoading ? loadingLabel : label

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className={cn("h-9 w-9 shrink-0", className)}
          onClick={onClick}
          disabled={disabled || isLoading}
          aria-label={tooltipLabel}
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <BsDownload className="h-4 w-4" aria-hidden />
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{tooltipLabel}</TooltipContent>
    </Tooltip>
  )
}

type GridRefreshButtonProps = {
  onClick: () => void
  disabled?: boolean
  label?: string
  className?: string
}

export function GridRefreshButton({
  onClick,
  disabled = false,
  label = "Refresh",
  className,
}: GridRefreshButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className={cn("h-9 w-9 shrink-0", className)}
          onClick={onClick}
          disabled={disabled}
          aria-label={label}
        >
          <BsArrowClockwise className="h-4 w-4" aria-hidden />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  )
}

export function GridToolbarActions({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn("flex flex-wrap items-center justify-end gap-2", className)}>
      {children}
    </div>
  )
}
